import { useMemo, type ReactNode } from 'react';

import type { RadialSectorItem } from '@/components/radialControlPad/root/radialControlPad.types.ts';

import { radialPadLayout } from './radialControlPad.constants.ts';
import {
  computeCenterSectorLayouts,
  resolveSectorDisabled,
} from './useRadialPadRingContent/radialPadRing.functions.ts';
import { RadialSectorButton } from './useRadialPadRingContent/RadialSectorButton.tsx';
import { RadialSectorTooltip } from './useRadialPadRingContent/RadialSectorTooltip.tsx';

type RadialPadCenterContent = {
  svgNodes: ReactNode;
  tooltipNodes: ReactNode;
};

export type UseRadialPadCenterContentProps = {
  enabled?: boolean;
  sectors?: RadialSectorItem[];
  padRadius: number;
  sectorGapPx?: number;
  padDisabled?: boolean;
  holdRepeatDelayMs?: number;
  tooltipSectorId: string | null;
  onHoveredSectorChange: (sectorId: string | null) => void;
};

export const useRadialPadCenterContent = ({
  enabled = true,
  sectors,
  padRadius,
  sectorGapPx,
  padDisabled,
  holdRepeatDelayMs,
  tooltipSectorId,
  onHoveredSectorChange,
}: UseRadialPadCenterContentProps): RadialPadCenterContent | null => {
  return useMemo(() => {
    if (!enabled || sectors == null || sectors.length === 0) {
      return null;
    }

    const layouts = computeCenterSectorLayouts({ sectors, padRadius, sectorGapPx });
    const iconSize = radialPadLayout.icon.centerSize;
    const iconBox = radialPadLayout.icon.centerBox;

    return {
      svgNodes: (
        <>
          {layouts.map(({ sector, iconX, iconY, sectorPath }) => (
            <RadialSectorButton
              key={sector.id}
              d={sectorPath}
              icon={sector.icon}
              label={sector.label}
              iconX={iconX}
              iconY={iconY}
              iconSize={iconSize}
              iconBox={iconBox}
              disabled={resolveSectorDisabled(sector.disabled, padDisabled)}
              holdRepeatDelayMs={holdRepeatDelayMs}
              onStart={sector.onStart}
              onStop={sector.onStop}
              onPointerEnter={() => onHoveredSectorChange(sector.id)}
              onPointerLeave={() => onHoveredSectorChange(null)}
            />
          ))}
        </>
      ),
      tooltipNodes: (
        <>
          {layouts.map(({ sector, tooltipLeft, tooltipTop }) => (
            <RadialSectorTooltip
              key={sector.id}
              label={sector.label}
              left={tooltipLeft}
              top={tooltipTop}
              open={!padDisabled && tooltipSectorId === sector.id}
            />
          ))}
        </>
      ),
    };
  }, [
    enabled,
    sectors,
    padRadius,
    sectorGapPx,
    padDisabled,
    holdRepeatDelayMs,
    tooltipSectorId,
    onHoveredSectorChange,
  ]);
};
